/**
 * 對話框「點背景關閉」的判定（與 CS 前端 `src/lib/backdropClose.ts` 同一套邏輯）。
 *
 * ## 為什麼不能用 `@click.self`
 *
 * UIEvents 規範把 `click` 派發到 mousedown 與 mouseup 的**最近共同祖先**。所以在對話框內
 * 按下滑鼠選字、拖到卡片外才放開時，共同祖先就是 backdrop —— `@click.self` 命中，
 * 對話框直接關掉，選到一半的文字也沒了（2026-09-01 主人回報：複製文字時圈選超出對話框
 * 就會關閉）。
 *
 * ## 判準
 *
 * 「這一次點擊**從頭到尾都在背景**」才算數：
 * - `pointerdown` 落在 backdrop 自身 → 武裝
 * - `pointerup` 落在面板內 → 解除武裝（反向選字：背景按下、面板放開）
 * - `click` 時仍武裝且 target 是 backdrop → 才關
 *
 * CS 端那份還多了滾輪穿透與死區轉送（`installModalWheelGuard`）。admin console 的對話框
 * 目前沒有那個問題（內容短、單欄），所以這裡只搬關閉判定——需要時再整份移植。
 */

export interface BackdropCloseBinding {
  pointerdown: (e: PointerEvent) => void
  pointerup: (e: PointerEvent) => void
  click: (e: MouseEvent) => void
}

/**
 * 回傳一組事件 handler，供 backdrop 元素以 `v-on="backdropClose"` 綁定，取代 `@click.self`。
 */
export function useBackdropClose(onClose: () => void): BackdropCloseBinding {
  let armed = false
  return {
    pointerdown(e: PointerEvent) {
      // 只有從 backdrop 自身按下才武裝；從面板內按下（選字起點）不算。
      armed = e.target === e.currentTarget
    },
    pointerup(e: PointerEvent) {
      // 放開時落在面板內 → 解除武裝。
      if (e.target !== e.currentTarget) armed = false
    },
    click(e: MouseEvent) {
      const hit = armed && e.target === e.currentTarget
      armed = false
      if (hit) onClose()
    },
  }
}
